import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import CgDialog from './CgDialog';
import CgRadixButton from './CgRadixButton';
import { useTranslation } from 'react-i18next';

/**
 * CgConfirmDialog — the single confirmation grammar for destructive and
 * consequential row/bulk actions. Built on the Radix CgDialog so it inherits
 * focus trap, ESC, scroll-lock and outside-click for free, and reads as part
 * of the v2 design language everywhere it renders.
 *
 * Replaces the four legacy confirmation patterns (window.confirm, the
 * TableActionOverlay popover, the react-bootstrap "Confirm Delete" modal, and
 * the no-confirm-at-all single delete). Policy: every irreversible verb
 * confirms through this component; `window.confirm` is banned.
 *
 * Controlled component — the parent owns `open` and closes the dialog (usually
 * inside `onConfirm`, or when the mutation it fires settles). Pass `loading`
 * to show a spinner on the confirm button and lock the dialog while a mutation
 * is in flight.
 *
 * @param {boolean}  open
 * @param {(open:boolean)=>void} onOpenChange  Fired on cancel / ESC / overlay click.
 * @param {string}   title
 * @param {string}   [description]     Optional subtitle under the title.
 * @param {React.ReactNode} children   Body copy explaining the consequence.
 * @param {string}   [confirmLabel='Confirm']
 * @param {string}   [cancelLabel='Cancel']
 * @param {'danger'|'primary'|'success'} [confirmVariant='danger']  Confirm button
 *   colour — any CgRadixButton variant; the header glyph's tint follows it, so a
 *   dialog's confirm is never a different colour from the button that opened it.
 * @param {object}   [confirmIcon]     FontAwesome icon shown before the confirm label.
 * @param {object|false} [icon]       Override for the header accent glyph; `false`
 *   drops it, for a body that carries its own icons (one visual hierarchy).
 * @param {boolean}  [loading=false]
 * @param {()=>void} onConfirm
 * @param {number}   [width=460]
 * @param {number}   [zIndex]          Raise the stack when confirming from inside another CgDialog.
 */
export default function CgConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel,
  cancelLabel,
  confirmVariant = 'danger',
  confirmIcon,
  icon,
  loading = false,
  confirmDisabled = false,
  secondaryAction,
  onConfirm,
  width = 460,
  zIndex,
}) {
  const { t } = useTranslation('common');
  // Defaults resolved in the body, not in the parameter list: a parameter
  // default cannot call a hook, and a module-scope t() would freeze the copy
  // in whatever language was active at import time.
  const confirmText = confirmLabel ?? t('actions.confirm', 'Confirm');
  const cancelText = cancelLabel ?? t('actions.cancel', 'Cancel');

  const isDanger = confirmVariant === 'danger';
  const showAccent = icon !== false;
  const accentIcon = icon || (isDanger ? faExclamationTriangle : faCircleInfo);
  // Tint follows the verb: red for a destructive one, emerald for a state
  // transition, the brand for anything else. Spelled out per variant — an
  // interpolated class name is invisible to Tailwind's extractor.
  const ACCENT = {
    danger: ['bg-red-50', 'text-red-600'],
    success: ['bg-emerald-50', 'text-emerald-600'],
  };
  const [accentWrap, accentText] = ACCENT[confirmVariant] ?? [
    'bg-primaryColor/10',
    'text-primaryColor',
  ];

  const handleConfirm = () => {
    if (loading) return;
    onConfirm?.();
  };

  // While a mutation is in flight the dialog can't be dismissed by an accidental
  // outside click or close button — the confirm button owns the exit.
  return (
    <CgDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      width={width}
      zIndex={zIndex}
      closeOnOverlayClick={!loading}
      closeButton={!loading}
      footer={
        <div className="flex items-center justify-end gap-2">
          <CgRadixButton
            variant="secondary"
            disabled={loading}
            onClick={() => onOpenChange?.(false)}
            data-testid="confirm-dialog-cancel"
          >
            {cancelText}
          </CgRadixButton>
          {secondaryAction ? (
            <CgRadixButton
              variant="outline"
              icon={secondaryAction.icon}
              loading={secondaryAction.loading}
              loadingLabel={secondaryAction.loadingLabel}
              disabled={loading || secondaryAction.disabled}
              onClick={secondaryAction.onClick}
              data-testid={secondaryAction.testId ?? 'confirm-dialog-secondary'}
            >
              {secondaryAction.label}
            </CgRadixButton>
          ) : null}
          <CgRadixButton
            variant={confirmVariant}
            icon={confirmIcon}
            loading={loading}
            disabled={confirmDisabled}
            onClick={handleConfirm}
            data-testid="confirm-dialog-confirm"
          >
            {/* `confirmText`, the resolved default — this rendered the raw prop
                before, so a dialog with no explicit label had a blank button. */}
            {confirmText}
          </CgRadixButton>
        </div>
      }
    >
      <div
        className="flex items-start gap-3"
        data-testid="confirm-dialog"
      >
        {showAccent ? (
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${accentWrap}`}
          >
            <FontAwesomeIcon
              icon={accentIcon}
              className={`text-sm ${accentText}`}
            />
          </div>
        ) : null}
        <div className="flex-1 min-w-0 text-sm text-slate-700 leading-relaxed">{children}</div>
      </div>
    </CgDialog>
  );
}
